/**
 * SSE streaming client for STEWARD API.
 *
 * Uses POST-based fetch + ReadableStream (not EventSource, which only supports GET).
 * Handles automatic reconnection with exponential backoff.
 */

export interface ChatRequest {
  message: string;
  conversationId?: string;
}

export interface SSEEvent {
  event: string;
  data: Record<string, unknown>;
}

/** First event of every stream — carries the server-minted conversation id so
 *  the widget can target a Stop (cancel) immediately, including on turn 1 whose
 *  row does not exist yet. The late `metadata` event no longer carries the id. */
export interface ConversationEvent { conversationId: string }
/** Precedes the answer's tokens. `aiGenerated` is the machine-readable mark
 *  that the answer is the model's output: false when the server wrote the
 *  answer itself (the output guardrail's fallback, a canned line standing in
 *  for an empty answer, the stuck-retry reply). */
export interface MetadataEvent { messageId: string; aiGenerated?: boolean }
export interface SourceEvent { filename: string; title: string; headingPath: string; relevance: number; vectorMatch: boolean }
export interface TokenEvent { content: string }
export interface GuardrailEvent { type: string; passed: boolean; reason?: string; flags?: string[] }
/** Terminal event of a successful turn. `traceId` (32-hex) addresses the
 *  owned telemetry trace the server flushed before emitting this event —
 *  it is what the feedback endpoint keys on, so the widget stores it on
 *  the completed message. Degraded/timeout terminals omit it. */
export interface DoneEvent { usage: { promptTokens: number; completionTokens: number }; traceId?: string; elapsedMs?: number; degraded?: boolean }
export interface ErrorEvent { code: string; message: string }
/** Terminal acknowledgement of a server-side cancel (precedes the trailing
 *  `done`). `elapsedMs` is the turn total at cancellation (the collapsed-header
 *  aggregate source on this path, since no `responding` fires). */
export interface CancelledEvent { reason: string; elapsedMs?: number }
export interface ArtifactEvent { artifactId: string; imageUrl: string; downloadUrl: string; description: string }
/** A single phase the agent passes through while answering — fired as
 *  LangGraph nodes start (retriever, synthesizer) or as tools run. The
 *  widget renders these as a vertical "thinking" timeline above the
 *  assistant bubble, à la Claude's interim reasoning display.
 *
 *  - `retrieving` — knowledge-base search starting
 *  - `synthesizing` — synthesizer node starting (LLM call). Multiple
 *    iterations in a ReAct loop each emit one of these; the label
 *    flips from "Reasoning" to "Refining" after the first.
 *  - `tool_call` — agent invoked a tool (carries `tool` + `args`)
 *  - `tool_result` — tool returned (carries `tool`)
 *  - `tool_error` — a tool call that returned nothing: refused before it
 *    was carried out, or failed after it was (carries `tool`)
 *  - `responding` — synthetic terminal phase emitted just before the
 *    typewriter starts streaming the buffered answer. Makes the
 *    timeline's active dot pulse while tokens flow, instead of leaving
 *    "Reasoning"/"Refining" stuck-active during delivery.
 *
 *  `step` is a monotonic counter so the UI can sort/match across
 *  potentially out-of-order transports; `label` is the human string
 *  to display. */
export interface ThinkingEvent {
  step: number;
  /** The kinds the widget knows, and any other: the set is open, and a kind
   *  the widget does not know is shown by its label. */
  kind: 'retrieving' | 'synthesizing' | 'tool_call' | 'tool_result' | 'tool_error' | 'responding' | (string & {});
  label: string;
  tool?: string;
  args?: string;
  /** Server-stamped elapsed-at-emission (ms since the turn began). The widget
   *  renders a step's duration as the diff to the next step's elapsedMs. */
  elapsedMs?: number;
}

export interface StreamCallbacks {
  onConversation?: (data: ConversationEvent) => void;
  onMetadata?: (data: MetadataEvent) => void;
  onSource?: (data: SourceEvent) => void;
  onToken?: (data: TokenEvent) => void;
  onGuardrail?: (data: GuardrailEvent) => void;
  onArtifact?: (data: ArtifactEvent) => void;
  onThinking?: (data: ThinkingEvent) => void;
  onCancelled?: (data: CancelledEvent) => void;
  onDone?: (data: DoneEvent) => void;
  onError?: (data: ErrorEvent) => void;
}

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;

/** What a visitor reads when every attempt failed: by the last status, and
 *  whether the conversation gate already turned the stored conversation away. */
export function streamFailure(status: number | null, conversationDropped: boolean): string {
  // "This chat", never a product name: a host can title the widget its own.
  if (status === null) return 'The connection to this chat was lost. Please try again.';
  if (status === 429) return 'This chat is busy right now. Please try again in a moment.';
  if (status === 404 && conversationDropped) {
    return 'This conversation could not be continued. Please try again.';
  }
  if (status >= 500) return 'This chat is temporarily unavailable. Please try again.';
  // A 403 is a refused origin or a firewall rule; neither is the visitor's
  // to read, and neither is said by the status alone.
  return 'Something went wrong. Please try again.';
}

/**
 * What a visitor reads when the account behind the widget has run out of
 * insight credits.
 *
 * IT SAYS ALMOST NOTHING, AND THAT IS THE REQUIREMENT RATHER THAN A
 * SHORTAGE OF IDEAS. The billing bible's "Who sees what" is explicit about
 * this surface — "End users, in the widget. Never a billing message" — and
 * the live requirement gives the reason: the envelope is bare "because the
 * pk's reader is the operator's own visitor and a public surface must never
 * carry the operator's commercial posture"
 * (`plan-entitlements/spec.md`, the zero-credit refusal). That
 * governs the WORDS and not only the wire. A sentence saying a monthly
 * ceiling exists and has been hit is a billing message with the figures
 * taken out: it tells a stranger, on the operator's own site, that this
 * business is on a plan and has exhausted it.
 *
 * WRITTEN FOR THE OPERATOR'S VISITOR, who is not our customer and cannot
 * fix this. So nothing that reads as their fault, and nothing addressed to
 * someone who is not reading it — no tier, no plan, no "upgrade".
 *
 * NO DURATION, AND THE SECOND REASON IS AS STRONG AS THE FIRST. Naming one
 * names the plan's window, which the rule above forbids. It is also FALSE
 * under most of the ways this state ends. The operator can buy credits or
 * move tier and the chat answers again within the entitlement cache's
 * window, seconds, not weeks; a payment-degraded account takes Free's
 * allowance, and settling the card restores its own. The reset the widget
 * could name is the outer bound of those endings and the least likely one
 * to arrive first. A visitor told to return on the 1st also returns on
 * THEIR 1st: the allowance resets at 00:00 UTC, so a reader east of the
 * meridian comes back up to thirteen hours early and is refused again.
 *
 * "THIS CHAT" IS THE SUBJECT, and both halves of that are decided. The
 * widget's own not-connected line ("This chat is not connected to an API.")
 * is the house precedent, and the entity noun the corpus uses elsewhere is
 * not available here: "assistant" now names the message ROLE only, the
 * bubble and the typing indicator, after the owner's swap. "This chat"
 * also happens to be the only honest subject — the credits are spent
 * ACCOUNT-WIDE across every product, so a widget that has answered three
 * questions all month can be stopped by a busy sibling on another domain.
 * It is this chat that cannot answer; the pool was never this chat's.
 *
 * NO CLAIM ABOUT WHY, which is what stops the misreadings. The sentence it
 * replaced said the chat had answered as many questions as it COULD, which
 * is a claim about capability where the truth is permission — read by a
 * visitor who had just asked something hard, it said "I do not know this",
 * and read by anyone else it said "this product is broken".
 *
 * NO "TRY AGAIN". Nothing a visitor does clears the state, so a press
 * only buys the same refusal. The converse is not owed: a refused press
 * costs the operator nothing, so the copy has no duty to DISSUADE one,
 * which is what buys the silence about duration above.
 */
const ALLOWANCE_EXHAUSTED_MESSAGE =
  "This chat can't answer right now. It will be available again later.";

const MESSAGE_TOO_LONG_FALLBACK =
  'Your message is too long. Please shorten it and try again.';

/** Read the user-facing text from the API's typed 413 "message too long"
 *  contract (`{ detail: { code, maxLength, message } }`). This is our own
 *  error shape — the server owns the limit and the wording — so the widget
 *  hardcodes neither. Falls back to a generic line if the body is absent or
 *  not that shape. */
async function tooLongMessage(response: Response): Promise<string> {
  let message = MESSAGE_TOO_LONG_FALLBACK;
  try {
    const detail = (await response.json())?.detail;
    if (typeof detail?.message === 'string' && detail.message) {
      message = detail.message;
    }
  } catch (err) {
    // Our API always sends JSON here; a non-JSON 413 means an upstream
    // (proxy / load-balancer body-size limit) answered instead. Log it and
    // fall back rather than swallowing it silently.
    console.warn('[STEWARD] 413 response body was not JSON; using fallback message', err);
  }
  return message;
}

export class StewardStreamClient {
  private apiEndpoint: string;
  private apiKey: string | null;
  private authToken: string | null;
  private abortController: AbortController | null = null;

  constructor(apiEndpoint: string, apiKey?: string | null, authToken?: string | null) {
    this.apiEndpoint = apiEndpoint.replace(/\/$/, ''); // Strip trailing slash
    this.apiKey = apiKey ?? null;
    this.authToken = authToken ?? null;
  }

  /** Update auth credentials (e.g., after token refresh). */
  setApiKey(key: string): void {
    this.apiKey = key;
  }

  setAuthToken(token: string): void {
    this.authToken = token;
  }

  /** Abort the current stream. */
  abort(): void {
    this.abortController?.abort();
    this.abortController = null;
  }

  /** Request server-side cancellation of an in-flight turn (best-effort,
   *  fire-and-forget). Deliberately does NOT abort the local stream — the
   *  running request acknowledges with a `cancelled` SSE event the caller
   *  needs to finalize the turn. The endpoint returns 202 on success; a
   *  non-2xx (e.g. an expired key → 401/403) or a network error is logged, not
   *  thrown — the request's own deadline still bounds it either way. */
  async cancel(conversationId: string): Promise<void> {
    const headers: Record<string, string> = {};
    if (this.apiKey) {
      headers['X-API-Key'] = this.apiKey;
    }
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }
    try {
      const response = await fetch(
        `${this.apiEndpoint}/v1/chat/${encodeURIComponent(conversationId)}/cancel`,
        { method: 'POST', headers },
      );
      if (!response.ok) {
        console.warn(`[STEWARD] Cancel request returned ${response.status}`);
      }
    } catch (e) {
      console.warn('[STEWARD] Cancel request failed:', e);
    }
  }

  /** Post thumbs feedback for one answered turn, addressed by the trace id
   *  the `done` event carried. Resolves on 2xx; throws on a non-2xx status
   *  (and on a network error) so the caller can revert its optimistic
   *  state. Same headers/base as cancel(). */
  async sendFeedback(
    conversationId: string,
    traceId: string,
    value: 'up' | 'down',
  ): Promise<void> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (this.apiKey) {
      headers['X-API-Key'] = this.apiKey;
    }
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }
    const response = await fetch(
      `${this.apiEndpoint}/v1/chat/${encodeURIComponent(conversationId)}/feedback`,
      { method: 'POST', headers, body: JSON.stringify({ traceId, value }) },
    );
    if (!response.ok) {
      throw new Error(`Feedback request returned ${response.status}`);
    }
  }

  /** Send a chat message and stream the response via callbacks. */
  async chat(request: ChatRequest, callbacks: StreamCallbacks): Promise<void> {
    this.abort(); // Cancel any previous stream
    this.abortController = new AbortController();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.apiKey) {
      headers['X-API-Key'] = this.apiKey;
    }
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    // Capture the server-minted conversation id from the first `conversation`
    // event so a retry re-sends it. Without this, a turn-1 retry (which begins
    // with conversation_id undefined) would mint a SECOND conversation on the
    // server. The request body is rebuilt per attempt below from this value.
    let conversationId: string | null = request.conversationId ?? null;
    const wrappedCallbacks: StreamCallbacks = {
      ...callbacks,
      onConversation: (data) => {
        conversationId = data.conversationId;
        callbacks.onConversation?.(data);
      },
    };

    let lastError: Error | null = null;
    // The last attempt's HTTP status, or null when it failed without one
    // (the connection dropped, or the stream broke mid-answer).
    let lastStatus: number | null = null;
    // One heal per call. The chat route's only application 404 is a
    // conversation id that EXISTS and belongs to another product — a
    // missing id never 404s (the server silently re-mints it), so the
    // reachable stale case is a foreign-product id in the persisted
    // session (its storage key is fixed per origin, not per product:
    // a page rebuilt against a different product's key inherits the
    // old page's conversation). The heal keys on the gate's typed
    // body, never on the bare status, so a transient infrastructure
    // 404 keeps the id and retries with full context instead of
    // forking the session.
    let staleConversationDropped = false;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      if (attempt > 0) {
        const delay = BASE_DELAY_MS * Math.pow(2, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      lastStatus = null;
      try {
        // Rebuilt each attempt: a retry after the `conversation` event must
        // resend the captured id so the server reuses the same conversation.
        const body = JSON.stringify({
          message: request.message,
          conversation_id: conversationId ?? undefined,
        });
        const response = await fetch(`${this.apiEndpoint}/v1/chat`, {
          method: 'POST',
          headers,
          body,
          signal: this.abortController.signal,
        });

        if (response.status === 401) {
          callbacks.onError?.({ code: 'AUTH_ERROR', message: 'Authentication failed' });
          return; // Don't retry auth errors
        }

        if (response.status === 413) {
          // The API rejects an over-length message with a dedicated 413 (its own
          // typed contract) before the stream starts — unambiguous, unlike a
          // generic 422. Surface the server's message and do not retry.
          callbacks.onError?.({
            code: 'MESSAGE_TOO_LONG',
            message: await tooLongMessage(response),
          });
          return;
        }

        if (response.status === 409) {
          // THE ONE BILLING STATE THAT REACHES A VISITOR. The platform's
          // floor is that no billing state darkens the widget; the account
          // having no insight credits left is the single ruled exception,
          // and this is it. The route raises 409 `{"detail": {"code":
          // "allowance-exhausted"}}` before any model call and before the
          // SSE response is built, so it arrives here as a plain HTTP error
          // like the two arms above rather than as a stream event.
          //
          // TERMINAL, AND THIS ONE IS NOT MERELY STYLE. The state clears by
          // the operator acting — buying credits, settling a failed payment,
          // moving tier — or by the month turning over, and not one of
          // those happens inside a retry budget. Three attempts seconds
          // apart buy three identical refusals. Never retry, and never
          // invite one — the copy below does not say "try again".
          //
          // GATED ON THE STATUS ALONE, matching the 401 and 413 arms rather
          // than the 404 arm's body sniff, and the choice is deliberate. A
          // status-only arm cannot silently stop matching. Were this to read
          // the body's `code` instead, a renamed code upstream would drop the
          // refusal through to the retry loop — a retry storm on a refusal
          // no retry can clear, with nothing on screen to say so. Wrong copy
          // on some future unrelated 409 is the cheaper failure, and a
          // visible one. `/v1/chat` raises no other 409 today; a second one
          // is a change that has to reach this arm.
          callbacks.onError?.({
            code: 'ALLOWANCE_EXHAUSTED',
            message: ALLOWANCE_EXHAUSTED_MESSAGE,
          });
          return;
        }

        if (response.status === 404 && conversationId != null && !staleConversationDropped) {
          let isConversationGate = false;
          try {
            const detail = (await response.json())?.detail;
            isConversationGate = detail === 'Conversation not found';
          } catch {
            // A 404 without the gate's JSON body is not the gate.
          }
          if (isConversationGate) {
            // Dead-session heal: drop the foreign id and retry with
            // none — the server mints a fresh conversation whose
            // `conversation` event replaces the stale id in the host's
            // persisted session through the onConversation path above.
            // Once only, and never counted against the retry budget
            // (an attempt-0 heal re-sends with no delay; a mid-budget
            // heal re-waits its tier's backoff once). A gate 404 that
            // recurs after the heal — a captured fresh id going
            // foreign mid-call — surfaces as the failure it is rather
            // than minting conversation after conversation.
            staleConversationDropped = true;
            conversationId = null;
            attempt -= 1;
            continue;
          }
        }

        if (!response.ok) {
          lastStatus = response.status;
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        if (!response.body) {
          throw new Error('Response body is null');
        }

        await this._processStream(response.body, wrappedCallbacks);
        return; // Success — exit retry loop

      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          return; // User cancelled
        }
        lastError = error instanceof Error ? error : new Error(String(error));

        if (attempt < MAX_RETRIES) {
          console.warn(`[STEWARD] Stream attempt ${attempt + 1} failed, retrying...`);
        }
      }
    }

    // All retries exhausted. The visitor reads this, and the host's
    // `steward-error` carries the same text: what happened in plain words,
    // never the status line or the browser's own exception message.
    if (lastError) console.warn('[STEWARD] Stream failed:', lastError.message);
    callbacks.onError?.({
      code: 'STREAM_FAILED',
      message: streamFailure(lastStatus, staleConversationDropped),
    });
  }

  private async _processStream(body: ReadableStream<Uint8Array>, callbacks: StreamCallbacks): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events from buffer
        const events = this._parseSSEBuffer(buffer);
        buffer = events.remaining;

        for (const event of events.parsed) {
          this._dispatchEvent(event, callbacks);
        }
      }

      // Flush any remaining data in the buffer as a final event
      if (buffer.trim()) {
        const final = this._parseSSEBuffer(buffer + '\n\n');
        for (const event of final.parsed) {
          this._dispatchEvent(event, callbacks);
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  private _parseSSEBuffer(buffer: string): { parsed: SSEEvent[]; remaining: string } {
    const parsed: SSEEvent[] = [];
    // Split on \n and strip \r (SSE servers may send \r\n line endings)
    const lines = buffer.split('\n');
    let currentEvent = '';
    let currentData = '';
    let remaining = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].replace(/\r$/, ''); // Strip trailing \r

      // If this is the last line and doesn't end with \n, it's incomplete
      if (i === lines.length - 1 && !buffer.endsWith('\n')) {
        remaining = lines[i]; // Keep original (with \r if present) for re-buffering
        break;
      }

      if (line === '') {
        // Empty line = end of event
        if (currentData) {
          try {
            parsed.push({
              event: currentEvent || 'message',
              data: JSON.parse(currentData),
            });
          } catch {
            console.warn('[STEWARD] Failed to parse SSE data:', currentData);
          }
        }
        currentEvent = '';
        currentData = '';
      } else if (line.startsWith('event:')) {
        currentEvent = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        const lineData = line.slice(5).trim();
        currentData = currentData ? currentData + '\n' + lineData : lineData;
      }
      // Ignore id: and retry: fields
    }

    return { parsed, remaining };
  }

  private _dispatchEvent(event: SSEEvent, callbacks: StreamCallbacks): void {
    try {
      switch (event.event) {
        case 'conversation':
          callbacks.onConversation?.(event.data as unknown as ConversationEvent);
          break;
        case 'metadata':
          callbacks.onMetadata?.(event.data as unknown as MetadataEvent);
          break;
        case 'source':
          callbacks.onSource?.(event.data as unknown as SourceEvent);
          break;
        case 'token':
          callbacks.onToken?.(event.data as unknown as TokenEvent);
          break;
        case 'guardrail':
          callbacks.onGuardrail?.(event.data as unknown as GuardrailEvent);
          break;
        case 'artifact':
          callbacks.onArtifact?.(event.data as unknown as ArtifactEvent);
          break;
        case 'thinking':
          callbacks.onThinking?.(event.data as unknown as ThinkingEvent);
          break;
        case 'cancelled':
          callbacks.onCancelled?.(event.data as unknown as CancelledEvent);
          break;
        case 'done':
          callbacks.onDone?.(event.data as unknown as DoneEvent);
          break;
        case 'error':
          callbacks.onError?.(event.data as unknown as ErrorEvent);
          break;
        default:
          console.debug('[STEWARD] Unknown event type:', event.event);
      }
    } catch (err) {
      console.error('[STEWARD] Error in callback for event:', event.event, err);
      callbacks.onError?.({
        code: 'CALLBACK_ERROR',
        message: 'Something went wrong while showing the answer. Please try again.',
      });
    }
  }
}
